import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { logger } from '@/lib/logger';

// Re-export types from centralized location
export type { Workspace, WorkspaceMember } from '@/types/workspace';

import { Workspace, WorkspaceMember } from '@/types/workspace';

const ACTIVE_WORKSPACE_KEY = 'luminjo_active_workspace';

export const useWorkspace = () => {
  const { user } = useAuth();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [activeWorkspace, setActiveWorkspace] = useState<Workspace | null>(null);
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [userRole, setUserRole] = useState<WorkspaceMember['role'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch user's workspaces
  const fetchWorkspaces = useCallback(async () => {
    if (!user) {
      setWorkspaces([]);
      setActiveWorkspace(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Get workspace IDs the user is a member of
      const { data: memberData, error: memberError } = await supabase
        .from('workspace_members')
        .select('workspace_id')
        .eq('user_id', user.id);

      if (memberError) throw memberError;

      if (!memberData || memberData.length === 0) {
        setWorkspaces([]);
        setActiveWorkspace(null);
        setLoading(false);
        return;
      }

      const workspaceIds = memberData.map(m => m.workspace_id);

      // Fetch workspaces
      const { data: workspacesData, error: workspacesError } = await supabase
        .from('workspaces')
        .select('*')
        .in('id', workspaceIds);

      if (workspacesError) throw workspacesError;

      setWorkspaces(workspacesData || []);

      // Restore active workspace from localStorage or use first one
      const savedWorkspaceId = localStorage.getItem(ACTIVE_WORKSPACE_KEY);
      const savedWorkspace = workspacesData?.find(w => w.id === savedWorkspaceId);

      if (savedWorkspace) {
        setActiveWorkspace(savedWorkspace);
      } else if (workspacesData && workspacesData.length > 0) {
        setActiveWorkspace(workspacesData[0]);
        localStorage.setItem(ACTIVE_WORKSPACE_KEY, workspacesData[0].id);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setWorkspaces([]);
      setActiveWorkspace(null);
      setLoading(false);
      return;
    }

    fetchWorkspaces();
  }, [user, fetchWorkspaces]);

  // Fetch members when active workspace changes
  useEffect(() => {
    if (!activeWorkspace || !user) {
      setMembers([]);
      setUserRole(null);
      return;
    }

    const fetchMembers = async () => {
      try {
        const { data, error } = await supabase
          .from('workspace_members')
          .select(`
            *,
            profile:profiles(id, first_name, last_name, avatar_url)
          `)
          .eq('workspace_id', activeWorkspace.id);

        if (error) throw error;

        const formattedMembers: WorkspaceMember[] = (data || []).map(m => ({
          id: m.id,
          workspace_id: m.workspace_id,
          user_id: m.user_id,
          role: m.role as WorkspaceMember['role'],
          joined_at: m.joined_at,
          profile: m.profile as WorkspaceMember['profile'],
        }));

        setMembers(formattedMembers);

        // Find user's role via Map lookup (O(1) vs .find O(n))
        const membersByUserId = new Map(formattedMembers.map(m => [m.user_id, m]));
        const userMember = membersByUserId.get(user.id);
        setUserRole(userMember?.role || null);
      } catch (err: unknown) {
        logger.warn('Failed to fetch workspace members', { error: err }, 'Workspace');
      }
    };

    fetchMembers();
  }, [activeWorkspace, user]);

  const switchWorkspace = useCallback((workspace: Workspace) => {
    setActiveWorkspace(workspace);
    localStorage.setItem(ACTIVE_WORKSPACE_KEY, workspace.id);
  }, []);

  const isOwnerOrAdmin = userRole === 'owner' || userRole === 'admin';

  // Function to refetch workspaces and update active workspace
  const refetchWorkspaces = useCallback(async () => {
    await fetchWorkspaces();
  }, [fetchWorkspaces]);

  return useMemo(() => ({
    workspaces,
    activeWorkspace,
    members,
    userRole,
    loading,
    error,
    switchWorkspace,
    isOwnerOrAdmin,
    refetchWorkspaces,
  }), [
    workspaces,
    activeWorkspace,
    members,
    userRole,
    loading,
    error,
    switchWorkspace,
    isOwnerOrAdmin,
    refetchWorkspaces,
  ]);
};
